// 2026-08-23 scale a11y — WCAG 2.2, keyboard nav, skip link, focus-visible
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { WifiOff } from "lucide-react";
import { useT } from "@/lib/use-i18n";
import { probeOnline } from "@/lib/client-online";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useFocusTrapDialog } from "@/components/chat/hooks/use-focus-trap-dialog";

/**
 * Vortixy ONLY works with an active internet connection. There is no offline
 * fallback, no PWA cache-first strategy, no service-worker-offline page.
 * If the browser goes offline, this guard shows a blocking overlay that
 * prevents any interaction until connectivity returns.
 *
 * This is intentional: every feature (Chat, payments, auth,
 * usage tracking) requires a round-trip to the server. Running offline
 * would create a false sense of capability and then fail silently.
 */

// 2026-09-27: endpoint y timeout viven en `@/lib/client-online` (SSOT).
// 2026-09-15 / Free-tier CPU budget: health rechecks are capped at 180s per
// foreground tab, matching the shared operations polling ceiling.
export const ONLINE_RECHECK_INTERVAL_MS = 180_000;
// 2026-09-21 perf-cpu (research 2026 thundering-herd + SWR dedup): ventana
// compartida 30s por tab para los ticks automáticos (intervalo + visibility).
// Eventos de red (offline) y Reintentar manual usan force (frescura
// justificada, one-shot). Cross-tab a propósito solo por tab (sin
// BroadcastChannel): el probe solo corre sin red confirmada o bloqueado, a
// 180s/techo + jitter 0-15s anti-herd; un canal cross-tab no ahorra medible.
let lastProbeAtMs = 0;
let lastProbeOk = true;
let inflightProbe: Promise<boolean> | null = null;
const PROBE_DEDUP_MS = 30_000;
const PROBE_JITTER_MS = 15_000;
/** Test-only: resetea la ventana compartida del probe entre tests. */
export function __resetOnlineProbeForTests() {
  lastProbeAtMs = 0;
  lastProbeOk = true;
  inflightProbe = null;
}

/**
 * 2026-09-27 falso-offline (raíz): la sonda vive en `@/lib/client-online`
 * (fail-open: cualquier HTTP = online; solo fallo de red = sin-red). Antes
 * se probaba `/api/health` con `res.ok` — su 503 honesto en degradado se
 * leía como "sin internet" y bloqueaba toda la app con red perfecta.
 */
async function probeConnectivity(): Promise<boolean> {
  return probeOnline();
}

// 2026-09-21 perf-cpu: in-flight compartido por tab (paridad fetchUsageShared
// + AdminUsersTab guardedTick) — un tick de intervalo solapado con un
// visibilitychange no duplica el fetch a la sonda.
function sharedProbe(): Promise<boolean> {
  if (!inflightProbe) {
    inflightProbe = probeConnectivity().finally(() => {
      inflightProbe = null;
    });
  }
  return inflightProbe;
}
export function OnlineGuard({
  children
}: {
  children: React.ReactNode;
}) {
  const t = useT();
  // 2026-09-11: nunca bloquear en el primer paint (SSR/hidratación) — el
  // overlay solo aparece tras confirmar sin-red con el probe.
  const [blocked, setBlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const seq = useRef(0);
  // 2026-09-13 canónico animaciones: entrada/salida animate-in/out
  // (paridad CookieConsent — solo transform+opacity, 150ms).
  const {
    shouldRender: shouldRenderOverlay,
    isLeaving: isLeavingOverlay
  } = useAnimatedOpen(blocked, 150);
  const probeAndSet = useCallback(async (force = false): Promise<boolean> => {
    // 2026-09-21 perf-cpu (research 2026 SWR dedupingInterval): los ticks
    // automáticos (intervalo + visibility) comparten ventana 30s por tab —
    // intervalo y visibility del mismo tab no duplican fetch. Force
    // (evento offline, primer paint offline, Reintentar manual): frescura
    // justificada, one-shot exento (ver comentario ?t= en probeConnectivity).
    if (!force && Date.now() - lastProbeAtMs < PROBE_DEDUP_MS) return lastProbeOk;
    const id = ++seq.current;
    const ok = await sharedProbe();
    if (id !== seq.current) return ok;
    lastProbeAtMs = Date.now();
    lastProbeOk = ok;
    setBlocked(!ok);
    return ok;
  }, []);
  useEffect(() => {
    // Estado inicial real (2026-09-11 P1): si el navegador arranca offline,
    // verificar antes de bloquear — no fiarse solo del flag. Arrancando
    // online no se gasta ningún request.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      void probeAndSet(true);
    }
    const handleOnline = () => {
      seq.current++;
      setBlocked(false);
    };
    // Evento `offline`: puede ser espurio — siempre se verifica con el
    // probe, nunca se bloquea solo por el evento.
    const handleOffline = () => {
      void probeAndSet(true);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [probeAndSet]);

  // Solo mientras está bloqueado: re-verificar cada 180 s
  // (ONLINE_RECHECK_INTERVAL_MS — techo CPU free-tier 2026-09-15; el "5 s"
  // anterior era un comentario rancio, el código siempre usó la constante)
  // y reanudar solo.
  // Sin intervalo cuando hay red (cero costo en el caso común).
  // 2026-09-13 CPU budget: el tick salta en tab oculta y se
  // re-sincroniza en visibilitychange (paridad OverloadBanner).
  useEffect(() => {
    if (!blocked) return;
    // 2026-09-21 cero-wakeups en oculta (paridad StatusDashboard): start/stop
    // por visibilitychange — en background no queda intervalo vivo (antes 1
    // wakeup/180s por tab aunque el tick saltara sin probe).
    let interval = 0;
    const tick = () => {
      // 2026-09-27 fail-open: el tick re-verifica con sonda SIEMPRE, sin
      // fiarse de `navigator.onLine` (miente tras sleep/VPN/portales).
      // 2026-09-21 jitter 0-15s anti-herd (research 2026 thundering-herd:
      // "add jitter to TTLs/retry intervals"): N tabs abiertas a la vez no
      // golpean la sonda en el mismo ms. El techo sigue ≥180s entre
      // ticks (AGENTS.md § Free-tier CPU budget); la ventana 30s de
      // probeAndSet absorbe el solape intervalo↔visibility.
      const jitterMs = Math.floor(Math.random() * PROBE_JITTER_MS);
      window.setTimeout(() => {
        if (typeof document !== "undefined" && document.hidden) return;
        void probeAndSet();
      }, jitterMs);
    };
    const start = () => {
      if (interval) return;
      interval = window.setInterval(tick, ONLINE_RECHECK_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        window.clearInterval(interval);
        interval = 0;
      }
    };
    const onVisible = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
        return;
      }
      start();
      // 2026-09-27 fail-open: al volver visible se re-verifica siempre con
      // sonda (el flag `navigator.onLine` puede mentir en falso). La ventana
      // 30s de probeAndSet evita duplicar el fetch con el tick.
      void probeAndSet();
    };
    if (typeof document === "undefined" || !document.hidden) start();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [blocked, probeAndSet]);
  const handleRetry = async () => {
    // 2026-09-11: reintentar = re-verificar, no recargar a ciegas (recargar
    // sin red muestra la página de error del navegador y pierde contexto).
    // Si el probe confirma red, el overlay se oculta solo.
    setChecking(true);
    try {
      await probeAndSet(true);
    } finally {
      setChecking(false);
    }
  };

  // 2026-09-13 a11y lote 2.1: foco en Reintentar + trap Tab mientras bloquea
  // (patrón chat-sidebar vía useFocusTrapDialog). El bloqueo offline no se
  // puede cerrar con Escape — Escape re-verifica la red (equivale a Reintentar).
  const overlayRef = useFocusTrapDialog(blocked, () => {
    void handleRetry();
  });
  return <>
      <span data-online-guard={blocked ? "offline" : "online"} hidden aria-hidden="true" />
      {children}
      {!shouldRenderOverlay ? null : <div ref={overlayRef} className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-[var(--background)] p-8 ${isLeavingOverlay ? "animate-out fade-out zoom-out-95 duration-150 motion-reduce:animate-none" : "animate-in fade-in zoom-in-95 duration-150 motion-reduce:animate-none"}`} role="alert" aria-live="assertive">
          <div className="grid size-16 place-items-center rounded-2xl bg-[var(--muted)]">
            <WifiOff className="size-8 text-[var(--danger)]" aria-hidden="true" />
          </div>
          {/* 2026-09-22 FASE 3 FIX tokens P2: h1 en text-display SSOT
              (antes font-serif 24px propio — research 2026 single-source
              type scale; text-display ya es la serif expresiva). */}
          <h1 className="text-display text-foreground">{t("offlineTitle")}</h1>
          <p className="max-w-md text-center text-[15px] leading-relaxed text-muted-foreground">
            {t("offlineBody")}
          </p>
          {/* Reintentar manual re-verifica la red — conserva ruta y borrador.
              Reusa errorRetry (existe en las 14 locales) para no añadir keys. */}
          <button type="button" onClick={handleRetry} disabled={checking} aria-busy={checking} className="mt-2 inline-flex min-h-11 items-center justify-center rounded-full bg-foreground px-8 py-3 text-[15px] font-semibold text-background transition-[transform,border-color] duration-200 hover:-translate-y-px active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:transform-none disabled:opacity-60">
            {t("errorRetry")}
          </button>
        </div>}
    </>;
}